import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { debounce } from "lodash";
import { createDiscountCardFetcher } from "../../api/price-prescriptions";
import PricePrescriptionsSearchForm from "./price-prescriptions-search-form";
import DrugPriceList from "./drug-price-list";
import DrugPriceMap from "./drug-price-map";

const PricePrescriptionsSearch = ({
  onSelectStart,
  onSelect,
  onValidation,
  components,
  prescription,
  zipCode,
  addToFavorite,
  favoriteList
}) => {
  const { Title, Loader, Text } = components;

  const [drugPrices, setDrugPrices] = useState([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [activeItem, setActiveItem] = useState("");
  const [selectedItem, setSelectedItem] = useState("");
  const [mapCenter, setMapCenter] = useState([0, 0]);
  const [collapsed, setCollapsed] = useState(false);
  const fetchDiscountCard = useMemo(createDiscountCardFetcher);

  useEffect(() => {
    if (window.innerWidth <= 768) {
      setCollapsed(true);
    } else {
      setCollapsed(false);
    }
  }, [window.innerWidth]);

  const handleSearch = drugPrices => {
    setDrugPrices(drugPrices);
    setLoadingSearch(false);
  };

  const handleSelect = drugPrice => {
    onSelectStart();
    fetchDiscountCard(drugPrice.DetailId)
      .then(res => onSelect([drugPrice, res.data.discountCard]))
      .catch(() => {});
  };

  const handleMarkerClick = debounce(({ PharmacyId, Latitude, Longitude }) => {
    setSelectedItem(PharmacyId === selectedItem ? "" : PharmacyId);
    setMapCenter([Latitude, Longitude]);
  }, 250);

  return (
    <>
      <Title title='Price prescriptions' />
      <div
        className={`price-prescriptions-web-sdk ${
          collapsed ? "price-prescriptions-collapsed" : ""
        }`}
      >
        <PricePrescriptionsSearchForm
          collapsed={collapsed}
          onCollapsedChange={setCollapsed}
          onSearchStart={() => setLoadingSearch(true)}
          onSearch={handleSearch}
          onSearchFailed={() => {
            setDrugPrices([]);
            setLoadingSearch(false);
          }}
          onValidation={onValidation}
          onClear={() => setDrugPrices([])}
          components={components}
          prescription={prescription}
          zipCode={zipCode}
        />
        {loadingSearch ? (
          <Loader />
        ) : drugPrices.length ? (
          <div className='price-prescriptions-search-results-web-sdk'>
            <DrugPriceMap
              drugPrices={drugPrices}
              mapCenter={mapCenter}
              activeItem={activeItem}
              selectedItem={selectedItem}
              onItemMouseEnter={({ PharmacyId }) => setActiveItem(PharmacyId)}
              onItemMouseLeave={() => setActiveItem("")}
              onItemClick={handleMarkerClick}
              components={components}
            />
            <DrugPriceList
              drugPrices={drugPrices}
              activeItem={activeItem}
              selectedItem={selectedItem}
              onItemMouseEnter={handleMarkerClick}
              onItemMouseLeave={() => setActiveItem("")}
              onItemClick={handleSelect}
              components={components}
              addToFavorite={addToFavorite}
              favoriteList={favoriteList}
            />
          </div>
        ) : (
          <Text>
            Type in the name of the drug you are trying to price compare. We
            will show you where you can find your prescription at the lowest
            retail cost. The prices do not reflect your current insurance rates.
          </Text>
        )}
      </div>
    </>
  );
};

export default PricePrescriptionsSearch;

PricePrescriptionsSearch.propTypes = {
  onSelectStart: PropTypes.func,
  onSelect: PropTypes.func,
  onValidation: PropTypes.func,
  components: PropTypes.object,
  prescription: PropTypes.string,
  zipCode: PropTypes.string,
  addToFavorite: PropTypes.func,
  favoriteList: PropTypes.arrayOf(PropTypes.string)
};
